/**
 * Ecommerce / checkout funnel analytics. Emits the GA4 recommended ecommerce
 * events (view_item_list → select_item/add_to_cart → begin_checkout →
 * add_payment_info → purchase) AND the matching Facebook Pixel standard events
 * (ViewContent → AddToCart → InitiateCheckout → AddPaymentInfo → Purchase,
 * plus Subscribe for recurring), each carrying value + currency so ad
 * campaigns can optimize for ROAS.
 *
 * GA and Pixel want DIFFERENT payload shapes for ecommerce (GA `items[]` vs
 * Pixel `content_ids`/`contents`), so these helpers fan out via the low-level
 * gaEvent/pixelEvent rather than the flat trackEvent taxonomy.
 *
 * Event names / params are product-flavoured (plan code + TWD amount) and must
 * NOT carry AI/STT provider or model names — see the no-leak guard.
 */
import { gaEvent, pixelEvent } from './track'
import type { SubscriptionCycle } from '@kiao-voice/core'

/** All charges are in New Taiwan Dollars. */
export const COMMERCE_CURRENCY = 'TWD'

export interface CommercePlan {
  /** Stable product code → GA `item_id` / Pixel content id. e.g. 'creator', 'pro', 'pack_medium'. */
  code: string
  /** The amount actually billed, in TWD. */
  priceTwd: number
  /** Human-readable label for reports; defaults to `code`. */
  name?: string
  /** Billing cycle for subscriptions (omitted for one-time packs). */
  cycle?: SubscriptionCycle
}

function gaItem(plan: CommercePlan): Record<string, unknown> {
  return {
    item_id: plan.code,
    item_name: plan.name ?? plan.code,
    price: plan.priceTwd,
    quantity: 1,
    ...(plan.cycle ? { item_variant: plan.cycle } : {}),
  }
}

function pixelContent(plan: CommercePlan): Record<string, unknown> {
  return {
    content_type: 'product',
    content_ids: [plan.code],
    content_name: plan.name ?? plan.code,
    contents: [{ id: plan.code, quantity: 1 }],
    value: plan.priceTwd,
    currency: COMMERCE_CURRENCY,
  }
}

/** Plans list shown (pricing page / plans modal). */
export function trackViewPlans(plans: CommercePlan[]): void {
  gaEvent('view_item_list', { item_list_name: 'plans', items: plans.map(gaItem) })
  pixelEvent('ViewContent', true, {
    content_type: 'product',
    content_ids: plans.map((p) => p.code),
  })
}

/** A specific plan picked (→ add to cart). */
export function trackSelectPlan(plan: CommercePlan): void {
  gaEvent('select_item', { item_list_name: 'plans', items: [gaItem(plan)] })
  gaEvent('add_to_cart', { currency: COMMERCE_CURRENCY, value: plan.priceTwd, items: [gaItem(plan)] })
  pixelEvent('AddToCart', true, pixelContent(plan))
}

/** Checkout (payment form) opened. */
export function trackBeginCheckout(plan: CommercePlan): void {
  gaEvent('begin_checkout', { currency: COMMERCE_CURRENCY, value: plan.priceTwd, items: [gaItem(plan)] })
  pixelEvent('InitiateCheckout', true, pixelContent(plan))
}

/** Card details submitted — payment info added, about to charge. */
export function trackAddPaymentInfo(plan: CommercePlan): void {
  gaEvent('add_payment_info', {
    currency: COMMERCE_CURRENCY,
    value: plan.priceTwd,
    items: [gaItem(plan)],
  })
  pixelEvent('AddPaymentInfo', true, pixelContent(plan))
}

export interface PurchaseArgs {
  plan: CommercePlan
  /** Unique id for GA4 purchase de-duplication (the TapPay trade id). MUST match
   *  the id the server sends to GA4 MP so the two aren't double-counted. */
  transactionId?: string | number
  /** Shared dedup id so the server-side CAPI `Purchase` collapses with this
   *  Pixel event. Generated client-side at submit and sent in the charge payload. */
  eventId?: string
}

/**
 * Payment succeeded — the conversion ad campaigns optimize toward.
 *
 * Fired only on the NON-3DS (synchronous) success path; 3DS purchases are
 * confirmed and emitted server-side. The Pixel `Purchase` carries `eventID` so
 * it deduplicates against the server CAPI `Purchase`. The Meta `Subscribe`
 * (predicted-LTV) signal is emitted server-side for every subscription (3DS and
 * non-3DS) — a single source — so it is intentionally NOT fired here.
 */
export function trackPurchase({ plan, transactionId, eventId }: PurchaseArgs): void {
  gaEvent('purchase', {
    ...(transactionId != null ? { transaction_id: String(transactionId) } : {}),
    currency: COMMERCE_CURRENCY,
    value: plan.priceTwd,
    items: [gaItem(plan)],
  })
  pixelEvent('Purchase', true, pixelContent(plan), eventId ? { eventID: eventId } : undefined)
}

export interface PurchaseFailedArgs {
  plan?: CommercePlan
  /** Coarse failure category for drop-off analysis (e.g. 'declined', 'tokenize_failed', 'error'). NOT a raw provider/error string. */
  reason?: string
}

/** Payment failed/declined — custom event for checkout drop-off analysis. */
export function trackPurchaseFailed({ plan, reason }: PurchaseFailedArgs): void {
  gaEvent('purchase_failed', {
    currency: COMMERCE_CURRENCY,
    ...(plan ? { value: plan.priceTwd, item_id: plan.code } : {}),
    ...(reason ? { reason } : {}),
  })
  pixelEvent('PurchaseFailed', false, {
    ...(plan ? { value: plan.priceTwd, currency: COMMERCE_CURRENCY, content_ids: [plan.code] } : {}),
    ...(reason ? { reason } : {}),
  })
}
